/**
 * LazyImage — 스크롤로 근처에 들어온 이미지만 실제로 받는다.
 *
 * 네이티브 `loading="lazy"` 를 쓰지 않는 이유:
 *   Chromium 의 지연 임계거리가 뷰포트 아래 약 1250px 로 고정이고 조절 수단이 없다.
 *   40px 썸네일 목록(행 높이 ~80px)이면 한 페이지 20건이 통째로 임계 안에 들어와
 *   스크롤 전에 전부 받아진다 — 세트 상세 그리드도 마찬가지다.
 *
 * root 를 뷰포트가 아니라 가장 가까운 스크롤 조상으로 잡는 이유:
 *   이 앱은 문서가 아니라 `.routeMotion`(Layout.module.css) 안에서 스크롤한다.
 *   root=viewport 로 두면 target 이 스크롤 컨테이너 clip 에 먼저 잘려 rootMargin 이
 *   사실상 먹지 않는다(= 화면에 들어와야 비로소 로드되어 이미지가 튄다).
 *
 * IntersectionObserver 가 없는 환경(구형 웹뷰·테스트)에서는 즉시 로드로 폴백한다.
 */
import { useEffect, useRef, useState } from "react";

/** 뷰포트 밖 이 거리 안까지는 미리 받는다. 스크롤 중 빈 칸이 보이지 않을 만큼만. */
const DEFAULT_ROOT_MARGIN = "300px";

export interface LazyImageProps {
  src: string;
  /** 장식용 이미지는 빈 문자열(기본값) — 카드명은 옆 텍스트가 이미 읽어준다. */
  alt?: string;
  className?: string;
  /** 미리 받기 시작할 거리. 기본 "300px". */
  rootMargin?: string;
}

/** 가장 가까운 스크롤 조상. 없으면 null(=뷰포트). */
function findScrollRoot(node: Element): Element | null {
  let parent = node.parentElement;
  while (parent) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return null;
}

export default function LazyImage({
  src,
  alt = "",
  className,
  rootMargin = DEFAULT_ROOT_MARGIN,
}: LazyImageProps) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(
    () => typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    if (shouldLoad) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { root: findScrollRoot(node), rootMargin, threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoad, rootMargin]);

  // src 미지정 img — 브라우저가 요청도 깨진 아이콘도 만들지 않는다.
  // 자리(높이)는 부모 박스(aspect-ratio)가 잡고 있어 레이아웃은 흔들리지 않는다.
  return (
    <img
      ref={ref}
      className={className}
      src={shouldLoad ? src : undefined}
      alt={alt}
      decoding="async"
    />
  );
}
